import { Col, Dropdown, Modal, Row, Space, Switch, Typography, message } from "antd";
import CompaniesView from "../components/CompaniesView";
import { useState } from "react";
import { Guid } from "guid-typescript";
import CompanyRegistrationFormWizard from "../components/CompanyRegistrationFormWizard";
import { getRegisterUsersTemplate } from "../repository/auth.repository";
import { SearchQuery } from "../code/search-query/search-query";
import {
  getCompaniesExport,
  getCompanyDepartmentsExport,
  getPlatformUsersExport,
  getArchivedCompanies,
  restoreCompany,
} from "../repository/data.repository";
import ArchivedListView from "../controls/ArchivedListView";
import { Company } from "../repository/data.default";
import Loader from "../controls/Loader";
import { CompanyViewCrumb } from "../components/CompaniesView.default";
import CompanyView from "../components/CompanyView";
import DepartmentView from "../components/DepartmentView";
import UserView from "../components/UserView";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function CompaniesPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [inputMode, setInputMode] = useState<
    "add" | "update" | "delete" | "view"
  >("add");
  const [inputOpen, setInputOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [inputUid, setInputUid] = useState<string | null | undefined>(
    {} as any,
  );
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [companyRegistrationInputOpen, setCompanyRegistrationInputOpen] =
    useState(false);
  const [crumb, setCrumb] = useState<CompanyViewCrumb>(null!);
  const [showArchived, setShowArchived] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  // Events

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onCrumb(crumb: CompanyViewCrumb): void {
    setCrumb(crumb);
  }

  function onInputOpen(
    mode: "add" | "update" | "delete" | "view",
    values?: {
      readonly uid: string;
    },
  ): void {
    setInputMode(mode);
    setInputOpen(true);
    setInputUid(values?.uid);
  }

  function onFinish(): void {
    setInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onClose(): void {
    setInputOpen(false);
  }

  function onCompanyRegistrationInputOpen(): void {
    setCompanyRegistrationInputOpen(true);
  }

  function onCompanyRegistrationInputFinish(): void {
    setTriggerView(Guid.create().toString());
  }

  function onCompanyRegistrationInputClose(): void {
    setCompanyRegistrationInputOpen(false);
  }

  function onDownloadRegisterTemplate(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading template file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getRegisterUsersTemplate()
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    switch (crumb.request) {
      case "search":
        getCompaniesExport({
          searchQuery,
          pageNumber: 0,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
          .then(() => {
            loading.destroy();
          })
          .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
        break;

      case "company":
        getCompaniesExport({
          searchQuery,
          pageNumber: 0,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
          .then(() => {
            loading.destroy();
          })
          .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
        break;

      case "subcompany":
        getCompaniesExport({
          searchQuery,
          pageNumber: 0,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
          .then(() => {
            loading.destroy();
          })
          .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
        break;

      case "department":
        getCompanyDepartmentsExport({
          searchQuery,
          pageNumber: 0,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
          .then(() => {
            loading.destroy();
          })
          .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
        break;
      case "user":
        getPlatformUsersExport({
          searchQuery,
          pageNumber: 0,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
          .then(() => {
            loading.destroy();
          })
          .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
        break;
    }
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      {/* Admin brief 2026-11-03 — toggle to switch between the active list
          and the archived (soft-deleted) rows + Restore. */}
      <Row align="middle" className="px-4 pt-3" gutter={8}>
        <Col>
          <Typography.Text type="secondary">Show archived</Typography.Text>
        </Col>
        <Col>
          <Switch
            size="small"
            checked={showArchived}
            onChange={setShowArchived}
          />
        </Col>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        {showArchived ? (
          <ArchivedListView<Company>
            entityLabel="company"
            loader={getArchivedCompanies as any}
            restore={restoreCompany}
            rowKey={(row) => row.uid}
            columns={[
              { title: "Name", dataIndex: "name" },
              { title: "Industry", dataIndex: "domainName", width: 180 },
              { title: "Email", dataIndex: "email", width: 220 },
              {
                title: "Deactivated",
                dataIndex: "deactivatedAt",
                width: 160,
                render: (v?: string | null) =>
                  v ? new Date(v).toLocaleString("en-GB") : "—",
              },
            ]}
          />
        ) : (
        <CompaniesView
          trigger={triggerView}
          title={"Companies"}
          panel={
            <Space className="flex flex-auto flex-row justify-end">
              {(crumb?.request == "company" ||
                crumb?.request == "subcompany" ||
                crumb?.request == "search") && (
                <Dropdown.Button
                  type="primary"
                  disabled={!permissions.can("Companies", "create")}

                  className={`${!permissions.can("Companies", "create") ? "disabled" : ""}`}
                  menu={{
                    items: [
                      {
                        // Audit fix 5 (2026-11-04) — previously
                        // onCompanyRegistrationInputOpen existed but no UI
                        // path called it. Surface the wizard here so the
                        // inline "Create role" + bulk user step is reachable.
                        key: "openRegistrationWizard",
                        label: "Registration Wizard",
                        disabled: !permissions.can("Companies", "create"),
                        onClick: onCompanyRegistrationInputOpen,
                      },
                      {
                        key: "downloadTemplate",
                        label: "Download Template",
                        disabled: !permissions.can("Companies", "create"),
                        onClick: onDownloadRegisterTemplate,
                      },
                      {
                        key: "downloadExport",
                        label: "Export to Excel",
                        disabled: !permissions.can("Companies", "view"),
                        onClick: onDownloadExport,
                      },
                    ],
                  }}
                  onClick={() => onInputOpen("add")}
                >
                  Add Company
                </Dropdown.Button>
              )}
              {crumb?.request == "department" && (
                <Dropdown.Button
                  type="primary"
                  disabled={!permissions.can("CompanyDepartments", "create")}

                  className={`${!permissions.can("CompanyDepartments", "create") ? "disabled" : ""}`}
                  menu={{
                    items: [
                      {
                        key: "downloadTemplate",
                        label: "Download Template",
                        disabled: !permissions.can("CompanyDepartments", "create"),
                        onClick: onDownloadRegisterTemplate,
                      },
                      {
                        key: "downloadExport",
                        label: "Export to Excel",
                        disabled: !permissions.can("CompanyDepartments", "view"),
                        onClick: onDownloadExport,
                      },
                    ],
                  }}
                  onClick={() => onInputOpen("add")}
                >
                  Add Department
                </Dropdown.Button>
              )}
              {crumb?.request == "user" && (
                <Dropdown.Button
                  type="primary"
                  className={`${!permissions.can("PlatformUsers", "view") ? "disabled" : ""}`}
                  menu={{
                    items: [
                      {
                        key: "downloadTemplate",
                        label: "Download Template",
                        disabled: !permissions.can("Companies", "create"),
                        onClick: onDownloadRegisterTemplate,
                      },
                      // {
                      //   key: "downloadExport",
                      //   label: "Export to Excel",
                      //   onClick: onDownloadExport,
                      // },
                    ],
                  }}
                  onClick={onDownloadExport}
                >
                  Export to Excel
                </Dropdown.Button>
              )}
            </Space>
          }
          onAction={(key: string, record: any) =>
            onInputOpen(key as any, record)
          }
          onSearch={onSearch}
          onCrumb={onCrumb}
        />
        )}
      </Col>
      <CompanyView
        mode={inputMode as any}
        open={
          inputOpen &&
          ["company", "subcompany", "search"].includes(crumb?.request) &&
          ["add", "update", "delete", "view"].includes(inputMode)
        }
        uid={inputUid}
        onFinish={onFinish}
        onClose={onClose}
      />
      <DepartmentView
        mode={inputMode as any}
        open={
          inputOpen &&
          crumb &&
          ["department"].includes(crumb?.request) &&
          ["add", "update", "delete", "view"].includes(inputMode)
        }
        uid={inputUid!}
        companyUid={crumb?.key}
        onFinish={onFinish}
        onClose={onClose}
      />
      <UserView
        mode={inputMode as any}
        open={
          inputOpen &&
          crumb &&
          ["user"].includes(crumb?.request) &&
          ["add", "update", "delete", "view"].includes(inputMode)
        }
        uid={inputUid!}
        onFinish={onFinish}
        onClose={onClose}
      />
      <CompanyRegistrationFormWizard
        open={companyRegistrationInputOpen}
        onFinish={onCompanyRegistrationInputFinish}
        onCancel={onCompanyRegistrationInputClose}
      />
      {contextHolder}
    </Col>
  );
}

export default CompaniesPage;
